import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import AdminLayout from '../../components/admin/AdminLayout';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';
import { sendNewEventEmail, sendEventCancelledEmail, isEmailConfigured } from '../../services/emailService';

function ManageSchedule() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { club } = useClub();
  
  // Use tenant-scoped data hooks
  const { data: events, loading, setData: setEvents } = useTenantCollection('events');
  const { data: teams } = useTenantCollection('teams');
  const { data: registrations } = useTenantCollection('registrations'); // For email recipients
  const { add, update, remove } = useTenantCrud('events');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    type: 'training',
    date: '',
    time: '',
    endTime: '',
    location: '',
    team: '',
    description: ''
  });

  const eventTypes = [
    { value: 'training', label: 'Training', color: '#10b981' },
    { value: 'match', label: 'Match', color: '#ef4444' },
    { value: 'tournament', label: 'Tournament', color: '#f59e0b' }
  ];

  const locations = [
    'ClubLink TT Training Ground',
    'Larry Gomes Stadium',
    'Hasely Crawford Stadium',
    'Ato Boldon Stadium',
    'Dwight Yorke Stadium',
    'Manny Ramjohn Stadium',
    'Marvin Lee Stadium',
    'Queen\'s Park Savannah',
    'Constantine Park',
    'Eddie Hart Ground'
  ];

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  // Sort events by date
  const sortedEvents = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'training',
      date: '',
      time: '',
      endTime: '',
      location: '',
      team: '',
      description: ''
    });
    setEditingEvent(null);
    setError('');
    setSuccess('');
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      type: event.type || 'training',
      date: event.date || '',
      time: event.time || '',
      endTime: event.endTime || '',
      location: event.location || '',
      team: event.team || '',
      description: event.description || ''
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    // Validation
    if (!formData.title || !formData.date || !formData.time) {
      setError('Please fill in all required fields');
      setSaving(false);
      return;
    }

    try {
      const eventData = {
        title: formData.title,
        type: formData.type,
        date: formData.date,
        time: formData.time,
        endTime: formData.endTime,
        location: formData.location,
        team: formData.team,
        description: formData.description
      };

      if (editingEvent) {
        // Update existing event (tenant-scoped)
        const updated = await update(editingEvent.id, eventData);
        setEvents(events.map(ev => 
          ev.id === editingEvent.id ? updated : ev
        ));
        setSuccess('Event updated successfully!');
      } else {
        // Create new event (tenant-scoped)
        const newEvent = await add(eventData);
        setEvents([...events, newEvent]);
        setSuccess('Event created successfully!');
        
        // Send email notifications if configured
        if (isEmailConfigured()) {
          // Get approved registrations with emails as recipients
          const approvedRegs = registrations.filter(r => r.status === 'approved' && r.email);
          if (approvedRegs.length > 0) {
            const recipients = approvedRegs.map(r => ({
              email: r.email,
              name: r.parentFirstName || 'Parent/Guardian'
            }));
            // Send in background, don't block
            sendNewEventEmail(newEvent, recipients, club?.name);
          }
        }
      }

      setTimeout(() => {
        setShowModal(false);
        resetForm();
      }, 1500);

    } catch (error) {
      console.error('Error saving event:', error);
      setError('Failed to save event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"?`)) return;

    try {
      // Get event data before deleting for email notification
      const eventToDelete = events.find(ev => ev.id === eventId);
      
      // Delete event (tenant-scoped)
      await remove(eventId);
      setEvents(events.filter(ev => ev.id !== eventId));
      
      // Send cancellation emails if configured
      if (isEmailConfigured() && eventToDelete) {
        const approvedRegs = registrations.filter(r => r.status === 'approved' && r.email);
        if (approvedRegs.length > 0) {
          const recipients = approvedRegs.map(r => ({
            email: r.email,
            name: r.parentFirstName || 'Parent/Guardian'
          }));
          // Send in background
          sendEventCancelledEmail(eventToDelete, recipients, club?.name);
        }
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const getEventTypeColor = (type) => {
    const eventType = eventTypes.find(t => t.value === type);
    return eventType ? eventType.color : '#9ca3af';
  };

  const formatDisplayTime = (time, endTime) => {
    if (!time) return '-';
    if (endTime) return `${time} - ${endTime}`;
    return time;
  };

  // Filter events
  const filteredEvents = sortedEvents.filter(event => {
    if (filterType !== 'all' && event.type !== filterType) return false;
    if (filterMonth !== 'all') {
      const eventMonth = new Date(event.date).getMonth();
      if (eventMonth !== parseInt(filterMonth)) return false;
    }
    return true;
  });

  // Separate upcoming and past events
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = filteredEvents.filter(event => new Date(event.date) >= today);
  const pastEvents = filteredEvents.filter(event => new Date(event.date) < today);

  return (
    <AdminLayout title="Manage Schedule" subtitle={`Create and manage training sessions, matches, and tournaments for ${club?.name || 'your club'}`}>
      <div className="admin-page-header">
        <div></div>
        <button className="btn-add" onClick={openAddModal}>
          + Add Event
        </button>
      </div>

          {/* Filters */}
          <div className="admin-filters">
            <div className="filter-group">
              <label>Event Type:</label>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="all">All Types</option>
                {eventTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Month:</label>
              <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
                <option value="all">All Months</option>
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
            </div>
            <div className="filter-stats">
              <span className="stat-pill">{upcomingEvents.length} upcoming</span>
              <span className="stat-pill secondary">{pastEvents.length} past</span>
            </div>
          </div>

          {/* Events List */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Upcoming Events</h2>
            </div>
            <div className="card-content">
              {loading ? (
                <p className="loading-text">Loading events...</p>
              ) : upcomingEvents.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📅</span>
                  <h3>No Upcoming Events</h3>
                  <p>Create your first event to get started.</p>
                  <button className="btn-add" onClick={openAddModal}>
                    + Add Event
                  </button>
                </div>
              ) : (
                <div className="schedule-event-list">
                  {upcomingEvents.map(event => (
                    <div key={event.id} className="schedule-event-card">
                      <div 
                        className="event-type-indicator" 
                        style={{ backgroundColor: getEventTypeColor(event.type) }}
                      ></div>
                      <div className="event-date-badge">
                        <span className="event-day">{new Date(event.date).getDate()}</span>
                        <span className="event-month">
                          {monthNames[new Date(event.date).getMonth()].slice(0, 3)}
                        </span>
                      </div>
                      <div className="event-info">
                        <h3>{event.title}</h3>
                        <div className="event-meta-row">
                          <span className="event-time-badge">
                            {formatDisplayTime(event.time, event.endTime)}
                          </span>
                          <span className="event-location-badge">{event.location || 'TBD'}</span>
                        </div>
                        <div className="event-tags">
                          <span 
                            className="event-type-tag"
                            style={{ backgroundColor: getEventTypeColor(event.type) }}
                          >
                            {event.type}
                          </span>
                          {event.team && <span className="event-team-tag">{event.team}</span>}
                        </div>
                      </div>
                      <div className="event-actions">
                        <button 
                          className="btn-attendance-link"
                          onClick={() => navigate(`/admin/attendance/${event.id}`)}
                          title="Take Attendance"
                        >
                          📋 Attendance
                        </button>
                        <button 
                          className="btn-edit"
                          onClick={() => openEditModal(event)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDelete(event.id, event.title)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Past Events */}
          {pastEvents.length > 0 && (
            <div className="admin-card" style={{ marginTop: '2rem' }}>
              <div className="card-header">
                <h2>Past Events</h2>
              </div>
              <div className="card-content">
                <div className="schedule-event-list past">
                  {pastEvents.slice(0, 10).map(event => (
                    <div key={event.id} className="schedule-event-card past">
                      <div 
                        className="event-type-indicator" 
                        style={{ backgroundColor: getEventTypeColor(event.type), opacity: 0.5 }}
                      ></div>
                      <div className="event-date-badge">
                        <span className="event-day">{new Date(event.date).getDate()}</span>
                        <span className="event-month">
                          {monthNames[new Date(event.date).getMonth()].slice(0, 3)}
                        </span>
                      </div>
                      <div className="event-info">
                        <h3>{event.title}</h3>
                        <div className="event-meta-row">
                          <span className="event-time-badge">
                            {formatDisplayTime(event.time, event.endTime)}
                          </span>
                        </div>
                      </div>
                      <div className="event-actions">
                        <button 
                          className="btn-attendance-link"
                          onClick={() => navigate(`/admin/attendance/${event.id}`)}
                          title="View/Edit Attendance"
                        >
                          📋 Attendance
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => handleDelete(event.id, event.title)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

      {/* Add/Edit Event Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent ? 'Edit Event' : 'Add New Event'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                {success && <div className="form-success">{success}</div>}

                <div className="form-group">
                  <label>Event Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., U16 Tier 1 Training"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Event Type *</label>
                    <select
                      value={formData.type}
                      onChange={e => setFormData({...formData, type: e.target.value})}
                      required
                    >
                      {eventTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Team</label>
                    <select
                      value={formData.team}
                      onChange={e => setFormData({...formData, team: e.target.value})}
                    >
                      <option value="">All Teams / General</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                      <option value="U8">U8</option>
                      <option value="U10">U10</option>
                      <option value="U12">U12</option>
                      <option value="U14">U14</option>
                      <option value="U16">U16</option>
                      <option value="U18">U18</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Date *</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={e => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Start Time *</label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={e => setFormData({...formData, time: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={e => setFormData({...formData, endTime: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={formData.location}
                    onChange={e => setFormData({...formData, location: e.target.value})}
                  >
                    <option value="">Select location...</option>
                    {locations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    placeholder="Add event details, instructions, or notes..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageSchedule;
